import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  OnInit,
} from "@angular/core";

import { Account } from "../../models/account.model";
import { StepInfo } from "../../models/step-info.model";
import { OutOptions } from "../../models/out-options.model";
import { StepState } from "../../models/step-state.model";
import { StepType } from "../../models/step-type.model";
import { User } from "../../models/user.model";
import { AccountService } from "../../services/account.service";
import { UserService } from "../../services/user.service";
import { Qna } from "../../models/qna.model";
import { PasswordState } from "../../models/password-state.model";
import { TranslateService } from "@ngx-translate/core";
import { SaveStatus } from "../../models/save-status.model";
import { QueryService } from "../../services/query.service";

@Component({
  selector: "app-as",
  templateUrl: "./account-setup.component.html",
  styleUrls: ["./account-setup.component.scss"],
  providers: [UserService, AccountService, QueryService],
})
export class AccountSetupComponent implements OnInit, AfterViewChecked {
  public stepInfo: StepInfo;
  public isNextBtnDisabled: boolean;
  public isDropdownVisible: boolean = true;
  private steps: { [type: number]: boolean } = {};
  public user: User;
  public account: Account;
  public detectedLanguage: string;
  public languages: Array<{ key: string; value: string }> = [
    { key: "en", value: "English" },
    { key: "ru", value: "Pусский" },
    { key: "de", value: "Deutsch" },
    { key: "es", value: "Español" },
    { key: "fr", value: "Français" },
    { key: "it", value: "Italiano" },
    { key: "ja", value: "日本語" },
    { key: "ko", value: "한국어" },
    { key: "pt", value: "Português" },
    { key: "zh-hans", value: "中文(简体)" },
  ];
  private togglesMap: Map<string, string>;
  public activeDirectoryMigrationEnabled: boolean = false;
  public auth0ChangePasswordUrl: string;
  public venueAppUsersUrl: string;
  public get isFirstStep(): boolean {
    return this.stepInfo.activeStepNumber === 0;
  }

  constructor(
    private userService: UserService,
    private accountService: AccountService,
    private changeDetectorRef: ChangeDetectorRef,
    private translate: TranslateService,
    private queryService: QueryService
  ) {}

  ngOnInit(): void {
    this.stepInfo = new StepInfo();
    this.isNextBtnDisabled = false;

    this.steps[StepType.ImportantInformation] = false;
    this.steps[StepType.YourProfile] = false;
    this.steps[StepType.AccountSecurity] = false;

    this.userService.getFeatureTogglesUrl().subscribe((data) => {
      this.venueAppUsersUrl = data.value;

      this.userService
        .getUserFeatureToggles(this.venueAppUsersUrl)
        .subscribe((data) => {
          this.togglesMap = data;
          this.activeDirectoryMigrationEnabled =
            this.togglesMap.get("ActiveDirectoryMigrationEnabled") == "1"
              ? true
              : false;
        });
    });

    this.setAccountModel();
    this.setUser();
  }

  ngAfterViewChecked() {
    this.changeDetectorRef.detectChanges();
  }

  private setUser(): void {
    const resetGuid = this.queryService.getGuid();
    this.userService.getUser(resetGuid).subscribe(
      (data) => {
        this.user = data;
        this.account.pdfLanguage = this.setPdfLanguage();
        this.account.email = this.user.email;
        this.account.resetGuid = this.user.guid;
        this.translate.use(this.setLanguage());
      },
      (error) => {
        this.user = error;
      }
    );
  }
  private setPdfLanguage(): string {
    if (navigator.languages) {
      const browserLg = navigator.languages[0]
        .trim()
        .substr(0, 2)
        .toLowerCase();
      let lgToSet = this.languages.find(
        (x) =>
          x.key === browserLg || (browserLg === "zh" && x.key.includes("zh"))
      );
      if (lgToSet) {
        return lgToSet.key;
      }
    }
    return this.user.language;
  }

  private setLanguage(): string {
    if (navigator.languages) {
      const browserLg = navigator.languages[0]
        .trim()
        .substr(0, 2)
        .toLowerCase();
      let lgToSet = this.languages.find(
        (x) =>
          x.key === browserLg || (browserLg === "zh" && x.key.includes("zh"))
      );
      if (lgToSet) {
        this.detectedLanguage = this.convertLangToShow(lgToSet.key);
        this.user.language = lgToSet.key;
        this.updateLanguage(this.user);
        return lgToSet.key;
      }
    }
    this.detectedLanguage = this.convertLangToShow(this.user.language);
    return this.user.language;
  }

  public updateLangOnClick(event: { target: { value: string } }) {
    this.translate.use(this.convertLangToSet(event.target.value));
    this.user.language = this.convertLangToSet(event.target.value);
    this.account.pdfLanguage = this.convertLangToSet(event.target.value);
    this.updateLanguage(this.user);
  }

  private updateLanguage(user: User): void {
    this.userService.updateUserLanguage(user).subscribe(
      (data) => {
        this.user = data;
      },
      (error) => {
        console.error(error);
      }
    );
  }

  private convertLangToShow(lang: string): string {
    const found = this.languages.find((x) => x.key === lang);
    return found ? found.value : "";
  }

  private convertLangToSet(lang: string): string {
    const found = this.languages.find((x) => x.value === lang);
    return found ? found.key : "";
  }

  private setAccountModel(): void {
    this.account = new Account();
    this.account.qna = new Qna();
    this.account.outOptions = new OutOptions();
    this.account.passwordState = new PasswordState();
  }

  private mockData(): void {
    this.user = this.userService.getUserMock();
    this.account.pdfLanguage = this.setPdfLanguage();
    this.translate.use(this.setLanguage());
  }

  public onPageChanged(stepChangeValue: number): void {
    let skipUpdateAccountPageState = false;

    if (
      !this.user.isSSO &&
      this.activeDirectoryMigrationEnabled &&
      this.stepInfo.activeStepNumber == StepType.YourProfile
    ) {
      skipUpdateAccountPageState = true;
      this.userService
        .getAuth0ChangePasswordRequest(this.venueAppUsersUrl, this.user.email)
        .subscribe((data) => {
          this.auth0ChangePasswordUrl = data.ticket;
          this.saveAccountModel();
        });
      return;
    }

    if (!skipUpdateAccountPageState) {
      this.updateAccountPageState(stepChangeValue);
    }

    if (
      this.user.isSSO &&
      this.stepInfo.activeStepNumber === StepType.AccountSecurity
    ) {
      this.saveAccount();
      return;
    } else if (this.stepInfo.activeStepNumber > StepType.AccountSecurity) {
      this.saveAccount();
      return;
    }

    this.scrollToTop(stepChangeValue);
  }

  private saveAccount(): void {
    this.stayAtCurrentStep();
    this.saveAccountModel();
  }

  private stayAtCurrentStep(): void {
    this.stepInfo.activeStepNumber--;
    this.isNextBtnDisabled = true;
    this.isDropdownVisible = true;
  }

  private saveAccountModel(): void {
    this.accountService.Setup(this.account).subscribe(
      (data: SaveStatus) => {
        console.log("AccountService.Setup - Account model: " + this.account);
        console.log("AccountService.Setup - Data1:" + data);
        console.log("AccountService.Setup - Data2:" + data.redirectUrl);
        console.log(
          "AccountService.Setup - Auth0changepwurl:" +
            this.auth0ChangePasswordUrl
        );
        if (this.activeDirectoryMigrationEnabled && !this.user.isSSO) {
          window.location.href =
            this.auth0ChangePasswordUrl +
            "&lang=" +
            this.user.language +
            "&flow=enableaccount";
        } else {
          window.location.href = data.redirectUrl;
        }
      },
      (error) => {
        console.error(error);
      }
    );
  }

  private updateAccountPageState(stepChangeValue: number): void {
    this.stepInfo.activeStepNumber += stepChangeValue;
    this.isNextBtnDisabled =
      stepChangeValue < 0 || this.steps[this.stepInfo.activeStepNumber]
        ? true
        : false;
    this.isDropdownVisible =
      stepChangeValue < 0 || this.steps[this.stepInfo.activeStepNumber]
        ? true
        : false;
  }

  public onStepChanged(stepKeyName: string): void {
    this.stepInfo.activeStepKeyName = stepKeyName;
  }

  public onStepModelChanged(stepState: StepState): void {
    this.isNextBtnDisabled = stepState.validState;
    this.steps[stepState.stepType] = stepState.validState;
  }

  private scrollToTop(stepChangeValue: number): void {
    if (stepChangeValue > 0) {
      window.scrollTo(document.documentElement.scrollLeft, 0);
    }
  }
}
